import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ActivityIndicator } from 'react-native';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../config/firebase';
import {
  APPROVAL_CATEGORY_KEYS,
  DEFAULT_CREDIT_ECONOMY,
  createCreditEconomyHelpers,
  formatWalletDollars,
  normalizeCreditEconomyConfig,
} from '../../utils/creditEconomyConfig';

function NumField({ label, hint, value, onChange, adminStyles, colors, suffix }) {
  return (
    <View style={{ marginBottom: 14 }}>
      <Text style={[adminStyles.notesLabel, { color: colors.textPrimary }]}>{label}</Text>
      {hint ? (
        <Text style={[adminStyles.devModeText, { fontSize: 12.5, marginBottom: 6 }]}>{hint}</Text>
      ) : null}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <TextInput
          value={value}
          onChangeText={onChange}
          keyboardType="decimal-pad"
          style={{
            flex: 1,
            borderWidth: 1,
            borderColor: colors.cardBorder,
            borderRadius: 8,
            paddingHorizontal: 12,
            paddingVertical: 10,
            color: colors.textPrimary,
            backgroundColor: colors.cardBgSubtle,
            fontSize: 15,
          }}
        />
        {suffix ? (
          <Text style={{ color: colors.textSecondary, fontSize: 13, maxWidth: 120 }}>{suffix}</Text>
        ) : null}
      </View>
    </View>
  );
}

export function AdminCreditEconomy({ adminStyles, colors, onNotify }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => ({ ...DEFAULT_CREDIT_ECONOMY }));

  useEffect(() => {
    const ref = doc(db, 'system', 'creditEconomy');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setDraft(normalizeCreditEconomyConfig(snap.exists() ? snap.data() : null));
        setLoading(false);
      },
      (err) => {
        console.warn('AdminCreditEconomy', err);
        setLoading(false);
      },
    );
    return () => unsub?.();
  }, []);

  const preview = useMemo(() => createCreditEconomyHelpers(draft), [draft]);

  const setCategoryUnits = useCallback((key, text) => {
    // Digits and one decimal point, max 2 decimal places; normalized to a number on save.
    const [whole = '', ...rest] = String(text).replace(/[^0-9.]/g, '').split('.');
    const n = rest.length ? `${whole}.${rest.join('').slice(0, 2)}` : whole;
    setDraft((prev) => ({
      ...prev,
      approvalCreditUnitsByCategory: {
        ...prev.approvalCreditUnitsByCategory,
        [key]: n,
      },
    }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const normalized = normalizeCreditEconomyConfig(draft);
      const auth = getAuth();
      await setDoc(
        doc(db, 'system', 'creditEconomy'),
        {
          ...normalized,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || null,
        },
        { merge: true },
      );
      setDraft(normalized);
      onNotify?.({ message: 'Credit economy saved', type: 'success' });
    } catch (err) {
      onNotify?.({ message: err?.message || 'Save failed', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setDraft({ ...DEFAULT_CREDIT_ECONOMY });
    onNotify?.({ message: 'Draft reset to defaults — click Save to apply', type: 'success' });
  };

  if (loading) {
    return <ActivityIndicator color={colors.textPrimary} style={{ marginVertical: 24 }} />;
  }

  const exampleCredits = 100;
  const exampleWallet = preview.convertCreditsCentsToWalletCents(exampleCredits);

  return (
    <View style={adminStyles.devModeSection}>
      <Text style={adminStyles.cardTitle}>Credit economy</Text>
      <Text style={[adminStyles.devModeText, { marginBottom: 12 }]}>
        Credits are always counted as whole Credits (never dollars). Only the monthly exchange to Wallet
        uses cash — default is 1 Credit → $0.01 Wallet.
      </Text>

      <View
        style={{
          padding: 12,
          borderRadius: 8,
          borderWidth: 1,
          borderColor: colors.cardBorder,
          backgroundColor: colors.cardBgSubtle,
          marginBottom: 16,
        }}
      >
        <Text style={{ color: colors.textPrimary, fontWeight: '600', marginBottom: 6 }}>Live preview</Text>
        <Text style={{ color: colors.textSecondary, fontSize: 13.5 }}>
          {preview.formatCreditExchangeRateLine()}
        </Text>
        <Text style={{ color: colors.textSecondary, fontSize: 13.5, marginTop: 4 }}>
          Example: {exampleCredits} Credits → {formatWalletDollars(exampleWallet)} Wallet
        </Text>
        <Text style={{ color: colors.textSecondary, fontSize: 13.5, marginTop: 4 }}>
          {preview.formatMaxCreditsExchangePerMonthLine()}
        </Text>
        <Text style={{ color: colors.textSecondary, fontSize: 13.5, marginTop: 4 }}>
          Default approval: {preview.creditEarnUnitsForApproval({})} Credits · Accessibility:{' '}
          {preview.creditEarnUnitsForApproval({ category: 'accessibility' })} Credits
        </Text>
      </View>

      <NumField
        label="Wallet cents per 1 Credit (exchange only)"
        hint="Default 1 → $0.01 Wallet per Credit. Dollar amounts apply only when converting to Wallet."
        value={String(draft.walletCentsPerCredit ?? 1)}
        onChange={(t) => setDraft((p) => ({ ...p, walletCentsPerCredit: t }))}
        suffix={formatWalletDollars(Math.max(0, Math.round(Number(draft.walletCentsPerCredit) || 0)))}
        adminStyles={adminStyles}
        colors={colors}
      />

      <NumField
        label="Max Credits to exchange per UTC month"
        hint="0 = no limit (still one exchange per UTC month). Example: 1100 Credits → up to $11.00 Wallet at default rate."
        value={String(draft.maxCreditsExchangePerMonth ?? 0)}
        onChange={(t) => setDraft((p) => ({ ...p, maxCreditsExchangePerMonth: t }))}
        suffix={Number(draft.maxCreditsExchangePerMonth) > 0 ? 'Credits' : 'Unlimited'}
        adminStyles={adminStyles}
        colors={colors}
      />

      <Text style={[adminStyles.notesLabel, { color: colors.textPrimary, marginTop: 8, marginBottom: 4 }]}>
        Credits granted on approval
      </Text>
      <Text style={[adminStyles.devModeText, { fontSize: 12.5, marginBottom: 8 }]}>
        Decimals allowed, up to 2 places (e.g. 0.5 or 1.25 Credits).
      </Text>
      {APPROVAL_CATEGORY_KEYS.map(({ key, label }) => (
        <NumField
          key={key}
          label={label}
          value={String(draft.approvalCreditUnitsByCategory?.[key] ?? 0)}
          onChange={(t) => setCategoryUnits(key, t)}
          suffix="Credits"
          adminStyles={adminStyles}
          colors={colors}
        />
      ))}

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
        <TouchableOpacity
          onPress={save}
          disabled={saving}
          style={[adminStyles.actionButton, { backgroundColor: "black", opacity: saving ? 0.6 : 1 }]}
        >
          {saving ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={adminStyles.actionButtonText}>Save credit economy</Text>
          )}
        </TouchableOpacity>
        <TouchableOpacity onPress={resetDefaults} style={[adminStyles.filterButton, { marginTop: 0 }]}>
          <Text style={adminStyles.filterButtonText}>Reset draft to defaults</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

export default AdminCreditEconomy;
